import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import { useAuditEvents } from 'api/audit';
import LoadingButton from 'components/shared/LoadingButton';
import { uniqBy } from 'lodash';
import React, { useMemo } from 'react';
import { AuditType, IAuditEvent } from 'types/api';
import AuditEventsTable from './AuditEventsTable';

interface IAuditEventsProps {
	type: AuditType;
	text: string;
	onEventClick: (event: IAuditEvent) => void;
}

const AuditEventsEmpty: React.FC<{ type: AuditType; text: string }> = ({
	type,
	text
}) => (
	<Stack alignItems="center" textAlign="center" gap={1} py={6}>
		<Typography variant="h3">
			{intl.tr(text ? 'audit.empty.noMatches' : 'audit.empty.title')}
		</Typography>
		{!text && (
			<Typography variant="body2" color="text.secondary">
				{intl.tr('audit.empty.hint')}
			</Typography>
		)}
		{!text && type === AuditType.Scim && (
			<Typography variant="body2" color="text.secondary">
				{intl.tr('audit.empty.scimHint')}
			</Typography>
		)}
	</Stack>
);

const AuditEvents: React.FC<IAuditEventsProps> = ({
	type,
	text,
	onEventClick
}) => {
	const { data, isLoading, isError, hasNextPage, fetchNextPage } =
		useAuditEvents(type, text);

	// Pages are offsets into a live log, so an event written between two fetches pushes the
	// previous page's last row onto the next one.
	const events = useMemo(
		() =>
			uniqBy(
				data?.pages.flatMap((page) => page.events || []),
				'id'
			),
		[data]
	);

	if (isLoading) {
		return (
			<Stack alignItems="center" py={6}>
				<CircularProgress data-testid="audit-loading" />
			</Stack>
		);
	}

	const loadError = (
		<Alert severity="error" variant="outlined" data-testid="audit-error">
			{intl.tr('audit.loadError')}
		</Alert>
	);

	// A failed "Load more" or background refetch keeps the pages already loaded.
	if (isError && !events.length) {
		return loadError;
	}

	if (!events.length) {
		return <AuditEventsEmpty type={type} text={text} />;
	}

	return (
		<Stack gap={3}>
			<AuditEventsTable events={events} onEventClick={onEventClick} />
			{isError && loadError}
			{hasNextPage && (
				<Stack alignItems="center">
					<LoadingButton
						variant="outlined"
						data-testid="audit-load-more"
						onClick={() => fetchNextPage()}
					>
						{intl.tr('audit.loadMore')}
					</LoadingButton>
				</Stack>
			)}
		</Stack>
	);
};

export default AuditEvents;
